import { FC, useRef, useState } from "react";

import { useForm } from "react-hook-form";

import { NewsletterFormSignupData } from "../Editor/types";

import { NewsletterFormSignUpStateless } from "./NewsletterFormSignUp.stateless";

import { AmplitudeEvents } from "@/constants/amplitude";
import { trpc } from "@/helpers/trpc";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { NewsletterFormFrontendPageOutput } from "@/types/trpc";

const extractCommunicationPreferenceIds = (
  preferences: NewsletterFormSignupData["communicationPreferences"]
): string[] => {
  if (!Array.isArray(preferences)) {
    return [];
  }

  return preferences
    .map((pref) => (typeof pref === "string" ? pref : pref.value))
    .filter((id): id is string => id !== null && id !== "");
};

export const NewsletterFormSignUp: FC<{
  newsletterForm: NewsletterFormFrontendPageOutput;
  isEmbedded?: boolean;
}> = ({ newsletterForm, isEmbedded }) => {
  const { logAmplitudeEvent } = useAmplitude();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: signupToNewsletterForm } = trpc.signupToNewsletterForm.useMutation();

  const form = useForm<NewsletterFormSignupData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      language: "",
      address: "",
      zipCode: "",
      city: "",
      state: "",
      country: "",
      communicationPreferences: [],
    },
  });

  const startTime = useRef(Date.now());

  const onSubmit = async (formData: NewsletterFormSignupData) => {
    setIsSubmitting(true);

    const processedPreferences = extractCommunicationPreferenceIds(formData.communicationPreferences);

    const { error } = await signupToNewsletterForm({
      newsletterFormId: newsletterForm.id,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      language: formData.language,
      address: formData.address,
      zipCode: formData.zipCode,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      communicationPreferences: processedPreferences,
    });

    if (error) {
      setIsSubmitting(false);
      return;
    }

    form.reset();

    setIsSubmitting(false);
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);

    logAmplitudeEvent(AmplitudeEvents.SignupFormSubmitted, {
      organizationId: newsletterForm.organization.id,
      formId: newsletterForm.id,
      formPath: newsletterForm.path,
      timeElapsed: Date.now() - startTime.current,
      isEmbedded,
    });
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} style={{ width: isEmbedded ? "100%" : "unset" }}>
      <NewsletterFormSignUpStateless
        mainColor={newsletterForm.mainColor}
        buttonColor={newsletterForm.buttonColor}
        title={newsletterForm.title}
        submitState={isSubmitted ? "submitted" : isSubmitting ? "submitting" : "idle"}
        form={form}
        isEmbedded={isEmbedded}
        hasFirstName={newsletterForm.hasFirstName}
        hasLastName={newsletterForm.hasLastName}
        hasPhoneNumber={newsletterForm.hasPhoneNumber}
        hasAddress={newsletterForm.hasAddress}
        hasLanguage={newsletterForm.hasLanguage}
        hasCommunicationPreferences={newsletterForm.hasCommunicationPreferences}
        communicationPreferences={newsletterForm.communicationPreferences}
      />
    </form>
  );
};
